<template>
  <div
    v-if="size"
    ref="eventEl"
    class="planner-event"
    :class="{
      [eventColorStyle]: true,
      isMoving,
      [eventStatus]: true,
      overlapping: overlappingIndex,
      isPassive,
      task: event.task,
      isPast: isPastEvent,
      [size]: true,
      noWrapped: visualHeight < 46,
      isTicked: isTaskCompleted,
      isSelected,
    }"
    @dblclick="onDblClick($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="event-separator" />

    <div class="drag-wrapper">
      <div class="infos">
        <div tabindex="0">
          <div class="calendar-border" :class="calendarBarClass" />

          <div class="header">
            <BaseCheckbox
              v-if="event.task"
              class="checkbox smooth"
              :is-checked="isTaskCompleted"
              :is-disabled="isBrokenIntegration || readOnly"
              :checkbox-size="checkboxSize"
              :checkbox-tooltip="taskCheckboxTooltip(isTaskCompleted, false)"
              @click="onCheckboxClick"
            />

            <div class="title smooth">
              <WarningIcon
                v-if="eventStatus === 'solo'"
                :class="['warning-icon', 'colored-icon', size]"
              />
              <span class="event-text">
                {{ titleDisplay }}
              </span>
            </div>
            <div v-if="visualHeight <= 60 || isAllDay" class="parent-context">
              <NotesIcon v-if="hasDescription" class="colored-icon" />
              <ContextButton
                v-if="task"
                :task="task"
                :task-context="task.context"
                :integration-status="task.integrationStatus"
                :is-shortcut-enabled="isSelected"
                :size="size === 'mini' || size === 'micro' ? 'xxs' : 'sm'"
              />
              <div
                v-if="parentTitle && visualHeight >= 30"
                class="hierarchy small-hierarchy"
              >
                {{ parentTitle }}
              </div>
            </div>
          </div>

          <div
            v-if="visualHeight >= 45 && withTime && !isAllDay"
            class="duration"
          >
            {{ time }}
          </div>
          <div
            v-if="eventLabels.length > 0 && visualHeight >= 30"
            class="event-labels"
          >
            <RTag
              v-for="label in eventLabels"
              :key="label.title"
              :text="eventLabels.length === 1 ? label.title : ''"
              :theme="label.theme"
              :tooltip="eventLabels.length > 1 ? label.title : undefined"
              skin="dot"
              :has-padding="false"
              compact
            />
          </div>
          <div v-if="visualHeight > 60 && !isAllDay" class="parent-context">
            <NotesIcon v-if="hasDescription" class="colored-icon" />
            <ContextButton
              v-if="task"
              :task="task"
              :task-context="task.context"
              :integration-status="task.integrationStatus"
              :is-shortcut-enabled="isSelected"
              :size="size === 'mini' || size === 'micro' ? 'xxs' : 'sm'"
            />
            <div v-if="parentTitle" class="hierarchy">
              {{ parentTitle }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Calendar } from "@fullcalendar/core";
import { addSeconds } from "date-fns/addSeconds";
import { find } from "lodash-es";
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";

import type {
  Attendee,
  EventId,
  FullEvent,
  FullTask,
  TaskId,
} from "@ctrl/controller-rpc";

import BaseCheckbox, { CheckboxSize } from "@/components/base/BaseCheckbox.vue";
import ContextButton from "@/components/elements/ContextButton.vue";
import RTag from "@/components/elements/RTag.vue";
import { useEventInfo } from "@/components/eventInfo";
import {
  colorClassFromSources,
  fetchColorSourceCell,
} from "@/components/graph/calendarColorSource";
import { databaseForTable } from "@/components/graph/predicates";
import { fetchExtensionRow } from "@/components/graph/query";
import { asGraphRecordResult } from "@/components/graph/result";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import WarningIcon from "@/components/icons/WarningIcon.vue";
import * as inject from "@/components/inject";
import { labelsAsTags } from "@/components/labels";
import { EVENTS_TABLE_ID, TASKS_TABLE_ID } from "@/components/tables";
import { taskCheckboxTooltip } from "@/components/task/checkbox-tooltip";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useCalendarColorSources } from "@/composables/useCalendarColorSources";
import { getDuration } from "@/event";
import { bindAsync, useReleaser } from "@/ref";
import { useParent } from "@/task";
import { pause } from "@/utils/pause";

import { getDaytime } from "../dates";
import { EventDetailsMode } from "./types";

export default defineComponent({
  components: {
    RTag,
    WarningIcon,
    NotesIcon,
    BaseCheckbox,
    ContextButton,
  },
  props: {
    calendarAPI: {
      type: Function as PropType<() => Calendar | undefined>,
      required: true,
    },
    end: { type: Date, required: true },
    event: { type: Object as PropType<FullEvent>, required: true },
    isLeftPaneShown: { type: Boolean, required: true },
    isMoving: { type: Boolean, required: true },
    isRightClickAllowed: { type: Boolean, required: true },
    isSelected: { type: Boolean, required: true },
    overlappingIndex: { type: Number, required: true },
    // Display-only event (see Planner.vue's `readOnly` prop): no task
    // completion, and the context menu is off by default (the host may
    // re-allow it via `isRightClickAllowed`), but double-click still opens
    // the item — even without a left pane, since there is no sidebar to
    // select into.
    readOnly: { type: Boolean, default: false },
    start: { type: Date, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
    withTime: { type: Boolean, default: true },
    // The hosting calendar's current zoom level (0 = default), threaded down
    // from PlannerCalendar — which owns the persisted, route-scoped zoom
    // state — so events reflow live when the user zooms the grid.
    zoomLevel: { type: Number, required: true },
  },
  emits: {
    open: (
      _v: { kind: "task" | "event"; id: TaskId | EventId },
      _mouseEvent?: MouseEvent
    ) => true,
  },
  async setup(props, { emit }) {
    // HACK: don't use event start or end properties
    const {
      calendarAPI,
      end,
      event,
      isLeftPaneShown,
      isRightClickAllowed,
      isSelected,
      readOnly,
      start,
      task,
    } = toRefs(props);
    const locale = inject.locale();
    const now = inject.clock();
    const controller = inject.controller();
    const calendars = inject.calendars();
    const contextMenu = inject.contextMenu();
    const profile = inject.profile();
    const theme = inject.theme();
    const types = inject.types();
    const releaser = useReleaser();
    const colorSources = releaser.add(useCalendarColorSources(profile));
    const browserStore = useBrowserStore(profile.value.user.accountId);
    releaser.defer(() => contextMenu.value.hide());
    const workspaceId = computed(() => profile.value.user.personal.workspaceId);
    const eventEl = ref<HTMLElement>();
    const isDraftEvent = computed(() => event.value.draft);
    const isAllDay = computed(() => event.value.allDay);
    const mode = computed(() =>
      isDraftEvent.value ? EventDetailsMode.create : EventDetailsMode.view
    );
    const isPastEvent = computed<boolean>(
      () =>
        // HACK: FullCalendar returns event.end as 03/01 00:00:00
        // to let us know that the event includes all the 02/01
        // then removing 1 second gives 02/01 23:59:59
        addSeconds(end.value, -1) <= now.value
    );
    const parentTitlePromise = releaser.add(
      useParent(controller, task).then(({ parentTitle }) => parentTitle)
    );
    const eventGraphDataPromise = releaser.add(
      bindAsync(event, (ev) =>
        fetchExtensionRow(
          controller,
          workspaceId.value,
          EVENTS_TABLE_ID,
          ev?.extension_row
        )
      )
    );
    // Color-source cells are fetched with a dedicated query (not read off
    // the extension row): a source may live several relation hops away, and
    // a row fetch only embeds related rows shallowly.
    const eventColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: event.value?.extension_row,
          source: colorSources.events.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            EVENTS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, EVENTS_TABLE_ID),
            }
          )
      )
    );
    const taskColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: task.value?.extension_row,
          source: colorSources.tasks.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            TASKS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, TASKS_TABLE_ID),
            }
          )
      )
    );
    const eventInfo = useEventInfo(controller, calendars, event);
    releaser.add(
      eventInfo.then((info) => ({ release: () => info.releaseEventInfo() }))
    );
    const [
      {
        calendar,
        calendarOwnerEmail,
        isPassive,
        isWritable,
        isMeetingWithMe,
        isMeetingAccepted,
      },
      eventGraphData,
      eventColorCell,
      taskColorCell,
      parentTitle,
    ] = await Promise.all([
      eventInfo,
      eventGraphDataPromise,
      eventColorCellPromise,
      taskColorCellPromise,
      parentTitlePromise,
    ]);
    // No calendar-switch watch needed: `calendar` is a live lookup in the
    // app-wide store keyed by `event.calendar` (see useEventInfo), so it
    // re-derives when the event moves to another calendar.
    const colorSourceArgs = computed(() => ({
      isAllocation: task.value != null,
      types: types.value,
      sources: {
        events: colorSources.events.value,
        tasks: colorSources.tasks.value,
      },
      eventCell: eventColorCell.value,
      taskCell: taskColorCell.value,
      store: browserStore,
      calendar: calendar.value,
    }));
    const eventColorStyle = computed(() =>
      colorClassFromSources({
        ...colorSourceArgs.value,
        theme: theme.value,
      })
    );
    const calendarBarClass = computed(() => {
      const color = calendar.value?.color;
      return color ? `option-color-${color}` : undefined;
    });
    const titleDisplay = computed(() => event.value.title || "Untitled");
    const time = computed<string>(() => {
      return `${locale.value.formatDaytime(getDaytime(start.value))} ––
            ${locale.value.formatDaytime(getDaytime(end.value))}`;
    });
    const eventStatus = computed(() => {
      if (!isMeetingWithMe.value) return "default";
      if (event.value.willAttend == null) return "noAnswer";
      if (
        !find(event.value.attendees, (attendee) => {
          return attendee.status !== "no";
        })
      )
        return "noOne";
      if (
        !find(event.value.attendees, (attendee: Attendee) => {
          return (
            attendee.status !== "no" &&
            calendarOwnerEmail.value &&
            !(attendee.email == calendarOwnerEmail.value)
          );
        })
      )
        return "solo";
      return event.value.willAttend;
    });

    // Labels
    const eventLabels = computed<Array<{ title: string; theme: string }>>(
      () => {
        const record = eventGraphData.value
          ? asGraphRecordResult(eventGraphData.value)
          : undefined;
        return labelsAsTags(browserStore, record?.Labels);
      }
    );

    const hasDescription = computed(
      () =>
        (event.value.notes && event.value.notes.blocks.length > 0) ||
        (task.value?.notes && task.value?.notes.blocks.length > 0) ||
        false
    );
    const isTaskCompleted = computed<boolean>(() => !!task.value?.completed);
    const onCheckboxClick = () => {
      if (readOnly.value || !task.value) return;
      controller.task.update(
        task.value.id,
        {
          completed: isTaskCompleted.value ? null : new Date(),
        },
        "app",
        null
      );
    };
    const checkboxSize = computed<CheckboxSize>(() => {
      switch (size.value) {
        case "micro":
          return "xxs";
        case "mini":
          return "xs";
        case "small":
        case "big":
        case "default":
        default:
          return "sm";
      }
    });

    // Calculate zoom multiplier based on slot duration
    // Default is 15 minutes per slot (zoom level 0)
    const zoomMultiplier = computed(() => {
      const ZOOM_SLOT_DURATIONS = [60, 30, 15, 10, 5]; // in minutes, matching zoom levels -2 to 2
      const index = Math.max(0, Math.min(4, props.zoomLevel + 2));
      const slotDuration = ZOOM_SLOT_DURATIONS[index];
      return 15 / slotDuration; // Normalize to default 15-minute slots
    });

    const height = computed(() =>
      isAllDay.value ? 0 : getDuration(start.value, end.value)
    );

    // Adjusted height accounts for zoom level to determine visual size
    const visualHeight = computed(() => height.value * zoomMultiplier.value);

    const size = computed<"micro" | "mini" | "small" | "big" | "default">(
      () => {
        if (visualHeight.value < 15 && !isAllDay.value) return "micro";
        if (
          visualHeight.value < 30 &&
          visualHeight.value >= 15 &&
          !isAllDay.value
        )
          return "mini";
        if (
          (visualHeight.value >= 30 && visualHeight.value < 45) ||
          isAllDay.value
        )
          return "small";
        if (visualHeight.value > 60 && !isAllDay.value) return "big";
        return "default";
      }
    );
    const onDblClick = (mouseEvent: MouseEvent) => {
      // Opening usually requires the left pane (double-click grew out of the
      // sidebar selection); a read-only calendar has no sidebar and
      // double-click is its only way to open an item.
      if ((isLeftPaneShown.value || readOnly.value) && !isDraftEvent.value)
        emit(
          "open",
          task.value
            ? { kind: "task", id: task.value.id }
            : { kind: "event", id: event.value.id },
          mouseEvent
        );
    };
    const showContextMenu = (e: MouseEvent) => {
      // `isRightClickAllowed` carries the host's resolved context-menu
      // policy (see Planner.vue's `allowContextMenu`), including the
      // read-only default — no `readOnly` check here.
      if (!isRightClickAllowed.value) return;
      if (!isSelected.value) eventEl.value?.click();
      if (task.value) {
        contextMenu.value.show(
          { type: "allocation", value: { ...task.value, event: event.value } },
          e
        );
      } else {
        contextMenu.value.show(
          {
            type: "event",
            value: {
              ...event.value,
              isCancellable: isMeetingAccepted.value,
              isDeletable: !!calendar.value?.isWritable,
            },
          },
          e
        );
      }
    };
    const isBrokenIntegration = computed(
      () => task.value?.integrationStatus?.status === "broken"
    );
    releaser.defer(
      watch(
        () => event.value.allDay,
        (is) => {
          if (is) pause(() => calendarAPI.value()?.updateSize());
        },
        { immediate: true }
      )
    );
    return {
      calendarBarClass,
      checkboxSize,
      eventColorStyle,
      eventEl,
      eventLabels,
      eventStatus,
      getDaytime,
      hasDescription,
      height,
      visualHeight,
      isAllDay,
      isBrokenIntegration,
      isPassive,
      isPastEvent,
      isTaskCompleted,
      isWritable,
      locale,
      mode,
      onCheckboxClick,
      onDblClick,
      parentTitle,
      showContextMenu,
      size,
      taskCheckboxTooltip,
      time,
      titleDisplay,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "sass:color";
.planner-event {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: content-box;
  z-index: $z-index-content-high;

  .drag-wrapper {
    border-radius: 3px;
    padding: 4px 6px;
    box-sizing: border-box;
    z-index: $z-index-content;
    overflow: hidden;
    height: 100%;

    :deep(.tippy-popper) {
      max-width: 236px;
      width: 236px;
    }
  }

  .warning-icon {
    height: 14px;

    &.mini {
      width: 12px;
      height: 12px;
      padding-top: 2px;
    }
  }

  .smooth {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: antialiased;
    font-smoothing: antialiased;
  }

  .infos {
    display: flex;
    flex-direction: column;
    height: 100%;

    // Select the tippy div wrapping the code
    & > :deep(div) {
      height: 100%;
    }

    .header {
      display: flex;
      padding: 0;
      column-gap: 6px;
    }

    .title {
      max-width: 100%;
      margin-top: 2px;
      display: flex;
      flex-direction: row;
      column-gap: 4px;
      min-height: 15px;
      overflow-wrap: break-word;

      .event-text {
        overflow: hidden;
        line-height: 16px;
        font-style: normal;
        @extend %font-event-medium;
      }

      .start {
        font-weight: normal;
        font-size: 10px;
        line-height: 19px;
        margin-inline-end: 6px;
      }
    }

    .event-labels {
      display: flex;
      align-items: center;
      gap: 3px;
      margin-top: 4px;

      :deep(.rtag.skin-dot.compact) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 80px;
        @extend %font-small-medium;

        .box {
          border: none;
          padding: 0;
          min-width: 0;
          min-height: 0;
        }

        .dot {
          width: 5px;
          height: 5px;
          min-width: 5px;
        }
      }
    }

    .parent-context {
      display: flex;
      flex-direction: row;
      column-gap: 6px;
      align-items: flex-start;
      justify-content: flex-start;
      margin-top: 2px;
    }

    .duration {
      font-weight: 400;
      font-size: 10px;
      line-height: 16px;
      margin-top: 3px;
    }
  }

  &:not(.isSelected).isPast .infos {
    opacity: 0.6;
  }

  &.maybe .drag-wrapper {
    @include tiles;
  }

  &.isPassive,
  &.noOne,
  &.no {
    &:not(.isSelected) .drag-wrapper {
      @include themed() {
        background-color: t($bg-event);
        border: 1px solid t($grey-15);
      }

      &:hover {
        @include themed() {
          background-color: t($grey-03);
        }
      }

      :deep(.colored-icon) path.fill {
        @include themed() {
          fill: t($grey-50);
        }
      }

      .event-text,
      .duration {
        @include themed() {
          color: t($grey-50);
        }
      }
    }
  }

  &.no .drag-wrapper .event-text {
    text-decoration: line-through;
  }

  .calendar-border {
    width: 3px;
    height: 16px;
    position: absolute;
    top: 6px;
    border-radius: 10px;
    inset-inline-start: 0;
    @include generate-calendar-mid-colors;
  }

  .event-separator {
    border-radius: 3px;
    inset-inline-start: 0;
    inset-inline-end: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    opacity: 0.4;
  }

  &.isSelected {
    :deep(.colored-icon) path.fill {
      fill: white;
    }

    &.calendar-a--light .drag-wrapper,
    &.calendar-a--dark .drag-wrapper {
      background-color: $calendar-a-40;
      color: white;
    }

    &.calendar-b--light .drag-wrapper,
    &.calendar-b--dark .drag-wrapper {
      background-color: $calendar-b-40;
      color: white;
    }

    &.calendar-c--light .drag-wrapper,
    &.calendar-c--dark .drag-wrapper {
      background-color: $calendar-c-40;
      color: white;
    }

    &.calendar-d--light .drag-wrapper,
    &.calendar-d--dark .drag-wrapper {
      background-color: $calendar-d-40;
      color: white;
    }

    &.calendar-e--light .drag-wrapper,
    &.calendar-e--dark .drag-wrapper {
      background-color: $calendar-e-40;
      color: white;
    }

    &.calendar-f--light .drag-wrapper,
    &.calendar-f--dark .drag-wrapper {
      background-color: $calendar-f-40;
      color: white;
    }

    &.calendar-g--light .drag-wrapper,
    &.calendar-g--dark .drag-wrapper {
      background-color: $calendar-g-40;
      color: white;
    }

    &.calendar-h--light .drag-wrapper,
    &.calendar-h--dark .drag-wrapper {
      background-color: $calendar-h-40;
      color: white;
    }

    &.calendar-i--light .drag-wrapper,
    &.calendar-i--dark .drag-wrapper {
      background-color: $calendar-i-40;
      color: white;
    }
  }

  &:not(.isSelected):not(.isPassive):not(.no):not(.noOne) {
    &.solo,
    &.yes,
    &.default,
    &.maybe {
      .drag-wrapper {
        @include transition(background-color 200ms ease-out);
      }

      &.calendar-a--light .drag-wrapper {
        background-color: $calendar-a-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-a-20, $lightness: -1.5%);
        }
      }

      &.calendar-a--dark .drag-wrapper {
        background-color: $calendar-a-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-a-90, $lightness: 1.5%);
        }
      }

      &.calendar-b--light .drag-wrapper {
        background-color: $calendar-b-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-b-20, $lightness: -1.5%);
        }
      }

      &.calendar-b--dark .drag-wrapper {
        background-color: $calendar-b-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-b-90, $lightness: 1.5%);
        }
      }

      &.calendar-c--light .drag-wrapper {
        background-color: $calendar-c-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-c-20, $lightness: -1.5%);
        }
      }

      &.calendar-c--dark .drag-wrapper {
        background-color: $calendar-c-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-c-90, $lightness: 1.5%);
        }
      }

      &.calendar-d--light .drag-wrapper {
        background-color: $calendar-d-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-d-20, $lightness: -1.5%);
        }
      }

      &.calendar-d--dark .drag-wrapper {
        background-color: $calendar-d-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-d-90, $lightness: 1.5%);
        }
      }

      &.calendar-e--light .drag-wrapper {
        background-color: $calendar-e-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-e-20, $lightness: -1.5%);
        }
      }

      &.calendar-e--dark .drag-wrapper {
        background-color: $calendar-e-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-e-90, $lightness: 1.5%);
        }
      }

      &.calendar-f--light .drag-wrapper {
        background-color: $calendar-f-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-f-20, $lightness: -1.5%);
        }
      }

      &.calendar-f--dark .drag-wrapper {
        background-color: $calendar-f-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-f-90, $lightness: 1.5%);
        }
      }

      &.calendar-g--light .drag-wrapper {
        background-color: $calendar-g-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-g-20, $lightness: -1.5%);
        }
      }

      &.calendar-g--dark .drag-wrapper {
        background-color: $calendar-g-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-g-90, $lightness: 1.5%);
        }
      }

      &.calendar-h--light .drag-wrapper {
        background-color: $calendar-h-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-h-20, $lightness: -1.5%);
        }
      }

      &.calendar-h--dark .drag-wrapper {
        background-color: $calendar-h-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-h-90, $lightness: 1.5%);
        }
      }

      &.calendar-i--light .drag-wrapper {
        background-color: $calendar-i-20;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-i-20, $lightness: -1.5%);
        }
      }

      &.calendar-i--dark .drag-wrapper {
        background-color: $calendar-i-90;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-i-90, $lightness: 1.5%);
        }
      }
    }

    &:not(.isSelected).noAnswer {
      .drag-wrapper {
        border-radius: 3px;

        @include themed() {
          background-color: t($bg);
        }

        &:not(.isMoving):hover {
          @include themed() {
            background-color: t($grey-05);
          }
        }
      }

      &.calendar-a--light .drag-wrapper {
        border: 1px solid $calendar-a-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-a-20, $lightness: 2%);
        }
      }

      &.calendar-a--dark .drag-wrapper {
        border: 1px solid $calendar-a-90;
      }

      &.calendar-b--light .drag-wrapper {
        border: 1px solid $calendar-b-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-b-20, $lightness: 2%);
        }
      }

      &.calendar-b--dark .drag-wrapper {
        border: 1px solid $calendar-b-90;
      }

      &.calendar-c--light .drag-wrapper {
        border: 1px solid $calendar-c-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-c-20, $lightness: 2%);
        }
      }

      &.calendar-c--dark .drag-wrapper {
        border: 1px solid $calendar-c-90;
      }

      &.calendar-d--light .drag-wrapper {
        border: 1px solid $calendar-d-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-d-20, $lightness: 2%);
        }
      }

      &.calendar-d--dark .drag-wrapper {
        border: 1px solid $calendar-d-90;
      }

      &.calendar-e--light .drag-wrapper {
        border: 1px solid $calendar-e-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-e-20, $lightness: 2%);
        }
      }

      &.calendar-e--dark .drag-wrapper {
        border: 1px solid $calendar-e-90;
      }

      &.calendar-f--light .drag-wrapper {
        border: 1px solid $calendar-f-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-f-20, $lightness: 2%);
        }
      }

      &.calendar-f--dark .drag-wrapper {
        border: 1px solid $calendar-f-90;
      }

      &.calendar-g--light .drag-wrapper {
        border: 1px solid $calendar-g-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-g-20, $lightness: 2%);
        }
      }

      &.calendar-g--dark .drag-wrapper {
        border: 1px solid $calendar-g-90;
      }

      &.calendar-h--light .drag-wrapper {
        border: 1px solid $calendar-h-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-h-20, $lightness: 2%);
        }
      }

      &.calendar-h--dark .drag-wrapper {
        border: 1px solid $calendar-h-90;
      }

      &.calendar-i--light .drag-wrapper {
        border: 1px solid $calendar-i-50;

        &:not(.isMoving):hover {
          background-color: color.adjust($calendar-i-20, $lightness: 2%);
        }
      }

      &.calendar-i--dark .drag-wrapper {
        border: 1px solid $calendar-i-90;
      }
    }

    &.calendar-a--light .drag-wrapper {
      color: $calendar-a-80;

      :deep(.colored-icon) path.fill {
        fill: $calendar-a-50;
      }
    }

    &.calendar-b--light .drag-wrapper {
      color: $calendar-b-80;

      :deep(.colored-icon) path.fill {
        fill: $calendar-b-50;
      }
    }

    &.calendar-c--light .drag-wrapper {
      color: $calendar-c-80;

      :deep(.colored-icon) path.fill {
        fill: $calendar-c-50;
      }
    }

    &.calendar-d--light .drag-wrapper {
      color: $calendar-d-80;

      :deep(.colored-icon) path.fill {
        fill: $calendar-d-50;
      }
    }

    &.calendar-e--light .drag-wrapper {
      color: $calendar-e-80;

      .colored-icon path.fill {
        fill: $calendar-e-50;
      }
    }

    &.calendar-f--light .drag-wrapper {
      color: $calendar-f-80;

      .colored-icon path.fill {
        fill: $calendar-f-50;
      }
    }

    &.calendar-g--light .drag-wrapper {
      color: $calendar-g-80;

      .colored-icon path.fill {
        fill: $calendar-g-50;
      }
    }

    &.calendar-h--light .drag-wrapper {
      color: $calendar-h-80;

      .colored-icon path.fill {
        fill: $calendar-h-50;
      }
    }

    &.calendar-i--light .drag-wrapper {
      color: $calendar-i-80;

      .colored-icon path.fill {
        fill: $calendar-i-50;
      }
    }
  }

  &.noOne .event-text {
    text-decoration: line-through;
  }

  &:not(.isPassive):not(.no):not(.noOne).overlapping {
    border-radius: 0 3px 3px 0;
    z-index: $z-index-content;
    border-radius: 3px;

    .drag-wrapper {
      margin: 1px 0 1px 1px;
    }
  }

  &.calendar-a--light,
  &.calendar-a--dark {
    .event-separator {
      background-color: $calendar-a-50;
    }
  }

  &.calendar-b--light,
  &.calendar-b--dark {
    .event-separator {
      background-color: $calendar-b-50;
    }
  }

  &.calendar-c--light,
  &.calendar-c--dark {
    .event-separator {
      background-color: $calendar-c-50;
    }
  }

  &.calendar-d--light,
  &.calendar-d--dark {
    .event-separator {
      background-color: $calendar-d-50;
    }
  }

  &.calendar-e--light,
  &.calendar-e--dark {
    .event-separator {
      background-color: $calendar-e-50;
    }
  }

  &.calendar-f--light,
  &.calendar-f--dark {
    .event-separator {
      background-color: $calendar-f-50;
    }
  }

  &.calendar-g--light,
  &.calendar-g--dark {
    .event-separator {
      background-color: $calendar-g-50;
    }
  }

  &.calendar-h--light,
  &.calendar-h--dark {
    .event-separator {
      background-color: $calendar-h-50;
    }
  }

  &.calendar-i--light,
  &.calendar-i--dark {
    .event-separator {
      background-color: $calendar-i-50;
    }
  }

  &.isMoving {
    opacity: 1;
  }

  &.task {
    .infos {
      .duration {
        margin-inline-start: 21px;
      }

      .title {
        font-weight: 400;
      }

      .hierarchy {
        width: fit-content;
        border-radius: 3px;
        @extend %font-small-medium;
        padding: 0 4px;
        white-space: nowrap;

        @include themed() {
          color: t($grey-50);
          background-color: t($grey-05);
        }
      }
    }
  }

  &.resizing .drag-wrapper {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  &:not(.small):not(.big):not(.mini):not(.micro) .header {
    max-height: calc(100% - 16px);
  }

  &.small {
    .parent-context {
      margin-top: 2px;
    }

    .checkbox {
      margin: 0;
      align-items: flex-start;
      padding-top: 3px;
    }
  }

  &.mini {
    .parent-context {
      margin-top: 1px;

      svg {
        width: 10px;
        height: 11px;
      }
    }

    .checkbox {
      align-items: flex-start;
      padding-top: 2px;
    }
  }

  &.big,
  &:not(.micro):not(.mini):not(.small) {
    .checkbox {
      margin-top: 3px;
      align-items: flex-start;
    }
  }

  &.big .header {
    max-height: calc(100% - 32px);
  }

  &.big {
    .infos .parent-context {
      margin-top: 2px;
    }

    &.task .infos .parent-context {
      margin-inline-start: 26px;
    }
  }

  &.noWrapped .event-text {
    white-space: nowrap;
  }

  /* 30 min events */
  &.mini,
  &.small {
    .infos {
      height: 100%;
    }

    .header {
      height: 100%;
      max-height: 100%;
    }
  }

  /* 10-15min events. 10 is micro, 15 is mini */
  &.micro,
  &.mini {
    .drag-wrapper {
      padding-inline-start: 4px;
    }

    .infos .header {
      max-height: 100%;
      column-gap: 4px;
    }

    .infos .title {
      margin: 0;
    }
  }

  &.micro {
    .infos .title {
      margin-top: 2px;
    }
  }

  &.mini {
    .calendar-border {
      height: 10px;
      top: 1px;
      width: 2px;
    }

    .infos {
      .title .event-text {
        padding-top: 1px;
        @extend %font-small-regular;
        line-height: 11px;
      }
    }
  }

  &.micro {
    border-bottom: 0;

    .calendar-border {
      height: 8px;
      top: 2.5px;
      width: 2px;
    }

    .infos {
      .title .event-text {
        padding-top: 0.5px;
        font-size: 9px;
        line-height: 7px;
      }
    }
  }

  &.calendar-placeholder .drag-wrapper {
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);

    @include themed() {
      border: 1px solid t($grey-30);
      background-color: t($bg);
    }
  }

  &.mini :deep(.handle.handle-bm),
  &.micro :deep(.handle.handle-bm) {
    bottom: -2px;
    height: 3px;
  }

  :deep(.handle.handle-bm) {
    bottom: -5px;
    inset-inline-start: 0px;
    cursor: ns-resize;
    height: 12px;
    position: absolute;
    width: 100%;
    z-index: $z-index-interactive;

    &:hover .handle-feedback {
      background: transparent;
      border-radius: 3px;
      height: 2px;
      margin-top: 12px;
    }
  }
}
</style>
